import React from 'react';

import {
  GUIDE_TEXT_MAX_WIDTH,
  GuideCallout,
  GuideMarkdown,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import {
  CollegeApplicationRoadmapFinalThoughts,
  CollegeApplicationRoadmapFooter,
} from '@backrs_ui/grow-ui/src/pages/CollegeApplicationRoadmapGuide/content';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type RoadmapFinalThoughtsProps = Readonly<{
  finalThoughts: CollegeApplicationRoadmapFinalThoughts;
  footer: CollegeApplicationRoadmapFooter;
}>;

export const RoadmapFinalThoughts: React.FC<RoadmapFinalThoughtsProps> = ({
  finalThoughts,
  footer,
}) => (
  <Box sx={containerSx}>
    <Typography variant="display-xs-semibold" component="h2" sx={titleSx}>
      {finalThoughts.title}
    </Typography>

    <Box sx={leadSx}>
      <GuideMarkdown>{finalThoughts.lead}</GuideMarkdown>
    </Box>

    <Box sx={gridSx}>
      {finalThoughts.thoughts.map((thought) => (
        <Box key={thought.id} sx={cardSx}>
          <Typography variant="text-md-semibold">{thought.title}</Typography>
          <Typography variant="text-sm-regular" sx={cardBodySx}>
            {thought.body}
          </Typography>
        </Box>
      ))}
    </Box>

    <GuideCallout
      variant={finalThoughts.callout.variant}
      title={finalThoughts.callout.title}
      body={finalThoughts.callout.body}
    />

    <Box sx={footerSx}>
      <Typography variant="text-xs-regular" sx={footerTextSx}>
        {footer.sources}
      </Typography>
      <Typography variant="text-xs-regular" sx={footerTextSx}>
        {footer.provenance}
      </Typography>
    </Box>
  </Box>
);

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  m: backrs.spacing['spacing-none'],
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const leadSx: SxProps<Theme> = {
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
};

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-2xl'],
  '@container (min-width: 600px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  p: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
});

const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  pt: backrs.spacing['spacing-xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const footerTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
